import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  FaTools, FaCamera, FaEnvelope, FaClipboardList,
  FaQuoteLeft, FaEye, FaArrowUp, FaArrowDown, FaCalendarAlt,
  FaClock, FaChartLine
} from 'react-icons/fa';

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [
        servicesRes, referencesRes, messagesRes,
        quotesRes, testimonialsRes, analyticsRes, activityRes
      ] = await Promise.all([
        api.get('/services/all').catch(() => ({ data: [] })),
        api.get('/references/all').catch(() => ({ data: [] })),
        api.get('/contact').catch(() => ({ data: [] })),
        api.get('/quotes').catch(() => ({ data: [] })),
        api.get('/testimonials/all').catch(() => ({ data: [] })),
        api.get('/analytics/stats').catch(() => ({ data: {} })),
        api.get('/activity-log?limit=8').catch(() => ({ data: { data: [] } })),
      ]);

      const messages = messagesRes.data;
      const quotes = quotesRes.data;
      const unreadMessages = Array.isArray(messages) ? messages.filter(m => !m.is_read).length : 0;
      const unreadQuotes = Array.isArray(quotes) ? quotes.filter(q => !q.is_read).length : 0;

      setStats({
        services: Array.isArray(servicesRes.data) ? servicesRes.data.length : 0,
        references: Array.isArray(referencesRes.data) ? referencesRes.data.length : 0,
        testimonials: Array.isArray(testimonialsRes.data) ? testimonialsRes.data.length : 0,
        unreadMessages,
        unreadQuotes,
      });

      setAnalytics(analyticsRes.data || {});
      setRecentActivity(activityRes.data?.data || activityRes.data || []);
      setRecentMessages(
        Array.isArray(messages) ? messages.slice(0, 5) : []
      );
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="admin-loading">Yukleniyor...</div>;

  const now = new Date();
  const greeting = now.getHours() < 12 ? 'Gunaydin' : now.getHours() < 18 ? 'Iyi Gunler' : 'Iyi Aksamlar';
  const dateStr = now.toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const statCards = [
    { label: 'Hizmetler', value: stats?.services || 0, icon: <FaTools />, color: '#86CD91', link: '/admin/hizmetler' },
    { label: 'Referanslar', value: stats?.references || 0, icon: <FaCamera />, color: '#e67e22', link: '/admin/referanslar' },
    { label: 'Müşteri Yorumları', value: stats?.testimonials || 0, icon: <FaQuoteLeft />, color: '#9b59b6', link: '/admin/yorumlar' },
    { label: 'Okunmamis Mesajlar', value: stats?.unreadMessages || 0, icon: <FaEnvelope />, color: '#e74c3c', link: '/admin/mesajlar', alert: true },
    { label: 'Teklif Istekleri', value: stats?.unreadQuotes || 0, icon: <FaClipboardList />, color: '#1abc9c', link: '/admin/teklifler', alert: true },
  ];

  const dailyChart = analytics?.daily_chart || [];
  const maxViews = Math.max(...dailyChart.map(d => d.count || 0), 1);

  return (
    <div className="dashboard">
      {/* Welcome Header */}
      <div className="dash-welcome">
        <div>
          <h2>{greeting}, {user?.username}!</h2>
          <p className="dash-date"><FaCalendarAlt /> {dateStr}</p>
        </div>
        <div className="dash-quick-stats">
          <div className="dash-quick-stat">
            <FaEye />
            <div>
              <strong>{analytics?.today_views || 0}</strong>
              <span>Bugun</span>
            </div>
          </div>
          <div className="dash-quick-stat">
            <FaChartLine />
            <div>
              <strong>{analytics?.weekly_views || 0}</strong>
              <span>Bu Hafta</span>
            </div>
          </div>
          {analytics?.monthly_comparison && (
            <div className="dash-quick-stat">
              {analytics.monthly_comparison.change >= 0 ? <FaArrowUp style={{ color: '#27ae60' }} /> : <FaArrowDown style={{ color: '#e74c3c' }} />}
              <div>
                <strong style={{ color: analytics.monthly_comparison.change >= 0 ? '#27ae60' : '#e74c3c' }}>
                  {analytics.monthly_comparison.change >= 0 ? '+' : ''}{analytics.monthly_comparison.change}%
                </strong>
                <span>Aylik</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stat Cards */}
      <div className="dash-stats-grid">
        {statCards.map((card, i) => (
          <Link to={card.link} key={i} className={`dash-stat-card ${card.alert && card.value > 0 ? 'pulse' : ''}`}>
            <div className="dash-stat-icon" style={{ background: card.color }}>{card.icon}</div>
            <div className="dash-stat-info">
              <h3>{card.value}</h3>
              <p>{card.label}</p>
            </div>
          </Link>
        ))}
      </div>

      {/* Charts + Recent */}
      <div className="dash-grid-2">
        {/* Mini Chart */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h3><FaChartLine /> Ziyaret Grafigi (30 Gun)</h3>
          </div>
          <div className="dash-chart">
            {dailyChart.length > 0 ? (
              <div className="dash-bar-chart">
                {dailyChart.map((day, i) => (
                  <div className="dash-bar-col" key={i}>
                    <div className="dash-bar-tooltip">{day.count}</div>
                    <div
                      className="dash-bar"
                      style={{ height: `${(day.count / maxViews) * 100}%` }}
                    />
                    <span className="dash-bar-label">
                      {new Date(day.date).getDate()}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="dash-empty">Henuz veri yok</div>
            )}
          </div>
        </div>

        {/* Top Pages */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h3><FaEye /> En Cok Ziyaret Edilen</h3>
          </div>
          <div className="dash-top-pages">
            {(analytics?.top_pages || []).length > 0 ? (
              analytics.top_pages.map((page, i) => (
                <div className="dash-top-page" key={i}>
                  <span className="dash-top-rank">#{i + 1}</span>
                  <span className="dash-top-path">{page.page_path}</span>
                  <span className="dash-top-count">{page.total_views}</span>
                </div>
              ))
            ) : (
              <div className="dash-empty">Henuz veri yok</div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Messages + Activity */}
      <div className="dash-grid-2">
        {/* Recent Messages */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h3><FaEnvelope /> Son Mesajlar</h3>
            <Link to="/admin/mesajlar" className="dash-card-link">Tumunu Gor</Link>
          </div>
          {recentMessages.length > 0 ? (
            <div className="dash-messages-list">
              {recentMessages.map(msg => (
                <div className={`dash-message-item ${!msg.is_read ? 'unread' : ''}`} key={msg.id}>
                  <div className="dash-message-info">
                    <strong>{msg.name}</strong>
                    <span>{msg.subject || 'Konu yok'}</span>
                  </div>
                  <span className="dash-message-time">
                    <FaClock /> {new Date(msg.created_at).toLocaleDateString('tr-TR')}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="dash-empty">Henuz mesaj yok</div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h3><FaClock /> Son Aktiviteler</h3>
            <Link to="/admin/aktivite" className="dash-card-link">Tumunu Gor</Link>
          </div>
          {Array.isArray(recentActivity) && recentActivity.length > 0 ? (
            <div className="dash-activity-list">
              {recentActivity.map(act => (
                <div className="dash-activity-item" key={act.id}>
                  <span className={`dash-activity-badge ${act.action}`}>{act.action}</span>
                  <div className="dash-activity-info">
                    <span><strong>{act.username}</strong> - {act.entity_type} {act.entity_title ? `"${act.entity_title}"` : ''}</span>
                    <small>{new Date(act.created_at).toLocaleString('tr-TR')}</small>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="dash-empty">Henuz aktivite yok</div>
          )}
        </div>
      </div>
    </div>
  );
}
